import { useState } from "react";
import { Loader2, Lock, RefreshCw } from "lucide-react";
import Seo from "@/components/Seo";

const ACCENT = "#4FA3D9";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "/adaptience-logo.png";

const METRICS = [
  { key: "website_visits", label: "Website Visits" },
  { key: "unique_visitors", label: "Unique Visitors" },
  { key: "simulation_starts", label: "Simulation Starts" },
  { key: "simulation_completions", label: "Simulation Completions" },
  { key: "practice_report_views", label: "Practice Report Views" },
  { key: "repeat_attempts", label: "Repeat Attempts" },
  { key: "feedback_submitted", label: "Feedback Submitted" },
];

export default function Admin() {
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(null);
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [members, setMembers] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [simFilter, setSimFilter] = useState("");
  const [sims, setSims] = useState([]);

  const load = async (pw, simId = simFilter) => {
    setLoading(true);
    setError("");
    const qs = simId ? `?simulation_id=${encodeURIComponent(simId)}` : "";
    try {
      // Global Analytics cards are never simulation-scoped; only the funnel uses the selector.
      const res = await fetch(`${API}/admin/stats`, { headers: { "X-Admin-Token": pw } });
      if (res.status === 401) {
        setError("Incorrect password.");
        setToken(null);
        setStats(null);
        return;
      }
      if (!res.ok) throw new Error("Request failed");
      const data = await res.json();
      setStats(data);
      setToken(pw);
      try {
        const sRes = await fetch(`${API}/admin/simulations`, { headers: { "X-Admin-Token": pw } });
        if (sRes.ok) { const sd = await sRes.json(); setSims(sd.items || []); }
      } catch { /* non-fatal */ }
      try {
        const fRes = await fetch(`${API}/admin/funnel${qs}`, { headers: { "X-Admin-Token": pw } });
        if (fRes.ok) { const fd = await fRes.json(); setFunnel(fd.steps || []); }
      } catch { /* non-fatal */ }
      try {
        const cRes = await fetch(`${API}/admin/contacts`, { headers: { "X-Admin-Token": pw } });
        if (cRes.ok) { const cd = await cRes.json(); setContacts(cd.items || []); }
      } catch { /* non-fatal */ }
      try {
        const mRes = await fetch(`${API}/admin/members/summary`, { headers: { "X-Admin-Token": pw } });
        if (mRes.ok) setMembers(await mRes.json());
      } catch { /* non-fatal */ }
    } catch {
      setError("Could not load stats. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onSimChange = (v) => { setSimFilter(v); if (token) load(token, v); };

  const updateStatus = async (id, status) => {
    setContacts((cs) => cs.map((c) => (c.id === id ? { ...c, status } : c)));
    try {
      await fetch(`${API}/admin/contacts/${id}/status?status=${encodeURIComponent(status)}`, {
        method: "POST",
        headers: { "X-Admin-Token": token },
      });
    } catch { /* non-fatal */ }
  };

  const exportContactsCsv = async () => {
    try {
      const res = await fetch(`${API}/admin/contacts/export`, { headers: { "X-Admin-Token": token } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "adaptience_contact_enquiries.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { /* non-fatal */ }
  };

  const exportMembersXlsx = async () => {
    try {
      const res = await fetch(`${API}/admin/members/export`, { headers: { "X-Admin-Token": token } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `adaptience-members-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { /* non-fatal */ }
  };

  const exportFeedbackXlsx = async () => {
    try {
      const qs = simFilter ? `&simulation_id=${encodeURIComponent(simFilter)}` : "";
      const res = await fetch(`${API}/admin/feedback/export?format=xlsx${qs}`, { headers: { "X-Admin-Token": token } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `adaptience-feedback-${simFilter || "all"}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { /* non-fatal */ }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (password.trim()) load(password.trim());
  };

  return (
    <div className="relative min-h-[100dvh] w-full overflow-y-auto bg-[#0a0a0a] text-white" data-testid="admin-page">
      <Seo
        title="Admin | Adaptience"
        description="Adaptience administration."
        canonical="/admin"
        robots="noindex, nofollow"
        ogImage={null}
      />
      <div className="pointer-events-none fixed inset-x-0 top-0 h-[50vh]">
        <div className="absolute left-1/2 top-[-10vh] h-[40vh] w-[60vw] -translate-x-1/2 rounded-full blur-[150px]" style={{ background: "rgba(79,163,217,0.08)" }} />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-2xl px-6 py-16 sm:px-10 sm:py-24">
        <div className="mb-12 flex items-center gap-3">
          <img src={LOGO} alt="Adaptience" onClick={() => { window.location.href = "/"; }} role="button" tabIndex={0} aria-label="Adaptience home" className="h-6 w-auto cursor-pointer" data-testid="admin-logo" />
          <span className="text-sm text-white/40">· Admin</span>
        </div>

        {!token ? (
          <form onSubmit={onSubmit} className="max-w-sm" data-testid="admin-login-form">
            <div className="mb-3 flex items-center gap-2 text-white/60">
              <Lock size={15} style={{ color: ACCENT }} />
              <span className="text-xs font-semibold uppercase tracking-[0.22em]">Admin access</span>
            </div>
            <h1 className="mb-6 font-display text-2xl font-semibold tracking-tight sm:text-3xl">Analytics</h1>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              data-testid="admin-password-input"
              className="w-full rounded-xl border border-white/12 bg-white/[0.03] px-4 py-3 text-base text-white outline-none transition-colors placeholder:text-white/35 focus:border-white/30"
              autoFocus
            />
            {error && <p className="mt-3 text-sm text-red-400" data-testid="admin-error">{error}</p>}
            <button
              type="submit"
              disabled={loading || !password.trim()}
              data-testid="admin-login-btn"
              className="mt-5 inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-transform active:scale-[0.98] disabled:opacity-40"
              style={{ background: ACCENT }}
            >
              {loading ? <Loader2 size={15} className="animate-spin" /> : null}
              View analytics
            </button>
          </form>
        ) : (
          <div data-testid="admin-dashboard">
            <div className="mb-8 flex items-center justify-between">
              <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Analytics</h1>
              <button
                onClick={() => load(token)}
                data-testid="admin-refresh-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/12 px-4 py-2 text-sm text-white/70 transition-colors hover:border-white/25 hover:text-white"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                Refresh
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {METRICS.map((m) => (
                <div
                  key={m.key}
                  data-testid={`stat-${m.key}`}
                  className="rounded-2xl border border-white/[0.08] bg-white/[0.02] px-6 py-5"
                >
                  <p className="text-sm text-white/50">{m.label}</p>
                  <p className="mt-2 font-display text-4xl font-semibold tracking-tight" style={{ color: ACCENT }}>
                    {stats?.[m.key] ?? 0}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-10" data-testid="simulation-funnel">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-display text-lg font-medium tracking-tight text-white">Simulation Funnel</h2>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    data-testid="admin-sim-selector"
                    value={simFilter}
                    onChange={(e) => onSimChange(e.target.value)}
                    className="rounded-lg border border-white/15 bg-white/[0.04] px-3 py-2 text-sm text-white/80 outline-none focus:border-white/30"
                  >
                    <option value="" className="bg-[#0a0a0a]">All Simulations</option>
                    {sims.map((s) => (
                      <option key={s.simulation_id} value={s.simulation_id} className="bg-[#0a0a0a]">{s.title}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={exportFeedbackXlsx}
                    data-testid="feedback-export-xlsx"
                    className="rounded-lg border border-white/15 bg-white/[0.04] px-3 py-2 text-sm text-white/80 transition-colors hover:bg-white/[0.08]"
                  >
                    Download Feedback Excel
                  </button>
                </div>
              </div>
              <div className="overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.02]">
                {funnel.length === 0 ? (
                  <p className="px-6 py-5 text-sm text-white/45" data-testid="funnel-empty">No funnel data yet.</p>
                ) : (
                  (() => {
                    const top = funnel[0]?.count || 0;
                    return funnel.map((s, i) => {
                      const pct = top > 0 ? Math.round((s.count / top) * 100) : 0;
                      const prev = i > 0 ? funnel[i - 1].count : null;
                      const drop = prev && prev > 0 ? Math.round(((prev - s.count) / prev) * 100) : null;
                      return (
                        <div
                          key={s.key}
                          data-testid={`funnel-step-${s.key}`}
                          className="border-b border-white/[0.05] px-6 py-3.5 last:border-b-0"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-white/75">{s.label}</span>
                            <span className="flex items-baseline gap-2">
                              {drop !== null && drop > 0 && (
                                <span className="text-xs text-white/35" data-testid={`funnel-drop-${s.key}`}>-{drop}%</span>
                              )}
                              <span className="font-display text-base font-semibold" style={{ color: ACCENT }}>{s.count}</span>
                            </span>
                          </div>
                          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/[0.06]">
                            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: ACCENT }} />
                          </div>
                        </div>
                      );
                    });
                  })()
                )}
              </div>
            </div>

            <div className="mt-10" data-testid="visitors-by-country">
              <h2 className="mb-4 font-display text-lg font-medium tracking-tight text-white">Visitors by Country</h2>
              <div className="overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.02]">
                {(stats?.visitors_by_country || []).length === 0 ? (
                  <p className="px-6 py-5 text-sm text-white/45" data-testid="country-empty">No visitor data yet.</p>
                ) : (
                  (stats.visitors_by_country || []).map((c, i) => (
                    <div
                      key={c.country + i}
                      data-testid={`country-row-${i}`}
                      className="flex items-center justify-between border-b border-white/[0.05] px-6 py-3.5 last:border-b-0"
                    >
                      <span className="text-sm text-white/75">{c.country}</span>
                      <span className="font-display text-base font-semibold" style={{ color: ACCENT }}>{c.visitors}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="mt-12" data-testid="members-summary">
              <div className="mb-4 flex items-center justify-between gap-4">
                <h2 className="font-display text-lg font-medium tracking-tight text-white">Members</h2>
                <button
                  onClick={exportMembersXlsx}
                  data-testid="members-export-xlsx"
                  className="inline-flex items-center gap-2 rounded-full border border-white/12 px-4 py-2 text-sm text-white/70 transition-colors hover:border-white/25 hover:text-white"
                >
                  Download Members Excel
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {[
                  { key: "total_members", label: "Total Members" },
                  { key: "members_with_completed_practice", label: "Members with Completed Practice" },
                  { key: "completed_practices_by_members", label: "Completed Practices by Members" },
                ].map((m) => (
                  <div key={m.key} data-testid={`members-${m.key}`} className="rounded-2xl border border-white/[0.08] bg-white/[0.02] px-6 py-5">
                    <p className="text-sm text-white/50">{m.label}</p>
                    <p className="mt-2 font-display text-4xl font-semibold tracking-tight" style={{ color: ACCENT }}>
                      {members?.[m.key] ?? 0}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-12" data-testid="contact-enquiries">
              <div className="mb-4 flex items-center justify-between gap-4">
                <h2 className="font-display text-lg font-medium tracking-tight text-white">
                  Contact Enquiries <span className="text-white/40">({contacts.length})</span>
                </h2>
                <button
                  onClick={exportContactsCsv}
                  data-testid="contacts-export-csv"
                  className="inline-flex items-center gap-2 rounded-full border border-white/12 px-4 py-2 text-sm text-white/70 transition-colors hover:border-white/25 hover:text-white"
                >
                  Export CSV
                </button>
              </div>

              {contacts.length === 0 ? (
                <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] px-6 py-5">
                  <p className="text-sm text-white/45" data-testid="contacts-empty">No enquiries yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {contacts.slice((page - 1) * 5, (page - 1) * 5 + 5).map((c) => {
                    const isOpen = expanded === c.id;
                    const status = c.status || "New";
                    return (
                      <div key={c.id} data-testid={`enquiry-${c.id}`} className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5">
                        <div className="flex cursor-pointer items-start justify-between gap-4" onClick={() => setExpanded(isOpen ? null : c.id)}>
                          <div className="min-w-0">
                            <p className="text-xs text-white/40">{fmtDate(c.created_at)}</p>
                            <p className="mt-1 truncate text-base font-medium text-white">{c.name}</p>
                            <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} className="text-sm transition-colors hover:opacity-80" style={{ color: ACCENT }}>{c.email}</a>
                            <p className="mt-2 text-sm text-white/55">
                              <span className="text-white/40">I am a:</span> {c.user_type_label || c.user_type}{c.user_type_other_detail ? <span data-testid={`enquiry-${c.id}-usertype-detail`}> ({c.user_type_other_detail})</span> : null} &nbsp;·&nbsp; <span className="text-white/40">Interested in:</span> {c.interest_label || c.interest}{c.interest_other_detail ? <span data-testid={`enquiry-${c.id}-interest-detail`}> ({c.interest_other_detail})</span> : null}
                              {c.country ? <> &nbsp;·&nbsp; <span className="text-white/40">Country:</span> {c.country}</> : null}
                            </p>
                          </div>
                          <span className="shrink-0 rounded-full border px-3 py-1 text-xs" style={{ borderColor: status === "New" ? ACCENT : "rgba(255,255,255,0.15)", color: status === "New" ? ACCENT : "rgba(255,255,255,0.55)" }}>{status}</span>
                        </div>

                        {isOpen && (
                          <div className="mt-4 border-t border-white/[0.07] pt-4">
                            <p className="whitespace-pre-wrap text-sm leading-relaxed text-white/75">{c.message}</p>
                            <div className="mt-4 flex flex-wrap items-center gap-2">
                              <span className="text-xs text-white/40">Mark as:</span>
                              {["New", "Read", "Responded"].map((s) => (
                                <button
                                  key={s}
                                  onClick={() => updateStatus(c.id, s)}
                                  data-testid={`status-${s.toLowerCase()}-${c.id}`}
                                  className="rounded-full border px-3 py-1 text-xs transition-colors"
                                  style={{ borderColor: status === s ? ACCENT : "rgba(255,255,255,0.14)", color: status === s ? ACCENT : "rgba(255,255,255,0.6)" }}
                                >
                                  {s}
                                </button>
                              ))}
                              <a href={`mailto:${c.email}`} className="ml-auto rounded-full px-4 py-1.5 text-xs font-semibold text-white" style={{ background: ACCENT }}>Reply</a>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {contacts.length > 5 && (
                <div className="mt-6 flex items-center justify-center gap-6 text-sm" data-testid="enquiries-pagination">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    data-testid="enquiries-prev"
                    className="text-white/70 transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    ← Previous
                  </button>
                  <span className="text-white/45" data-testid="enquiries-page-indicator">
                    Page {page} of {Math.ceil(contacts.length / 5)}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(Math.ceil(contacts.length / 5), p + 1))}
                    disabled={page >= Math.ceil(contacts.length / 5)}
                    data-testid="enquiries-next"
                    className="text-white/70 transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
